// src/utils/helpers.js

export const naturalSortKey = (str) => {
  if (str == null || str === '') return [];
  const s = String(str).trim();
  const parts = s.match(/(\d+|\D+)/g) || [s];
  return parts.map((p) => (/^\d+$/.test(p) ? parseInt(p, 10) : p.toLowerCase()));
};

/**
 * Ordenamiento natural: 1, 2, 3, 4... 10, 11 (no alfabético 1, 10, 11, 2, 3).
 * Maneja "46", "112", "Casa 1", "10B", etc.
 */
export const compareNatural = (a, b) => {
  const ka = naturalSortKey(a);
  const kb = naturalSortKey(b);
  for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
    const va = ka[i];
    const vb = kb[i];
    if (va === undefined) return -1;
    if (vb === undefined) return 1;
    if (typeof va !== typeof vb) {
      const sa = String(va);
      const sb = String(vb);
      if (sa < sb) return -1;
      if (sa > sb) return 1;
    } else if (typeof va === 'number') {
      if (va !== vb) return va < vb ? -1 : 1;
    } else {
      if (va !== vb) return va < vb ? -1 : 1;
    }
  }
  return 0;
};

/**
 * Comparador específico para número de propiedad/casa.
 * Orden numérico: 1, 2, 3... 10, 11, 46, 112, 190, 201.
 */
export const comparePropiedad = (a, b) => compareNatural(a, b);

/** Zona horaria alineada con el backend para fechas de pase (día civil). */
export const MEXICO_CITY_TZ = 'America/Mexico_City';

let cachedIntlMexicoCityWorks = null;
function intlMexicoCityTimezoneWorks() {
  if (cachedIntlMexicoCityWorks !== null) return cachedIntlMexicoCityWorks;
  try {
    if (typeof Intl === 'undefined' || !Intl.DateTimeFormat?.prototype?.formatToParts) {
      cachedIntlMexicoCityWorks = false;
      return false;
    }
    const probe = new Date(Date.UTC(2024, 6, 15, 18, 30, 0));
    const hourIn = (tz) =>
      new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: '2-digit', hour12: false })
        .formatToParts(probe)
        .find((p) => p.type === 'hour')?.value;
    const hMx = hourIn(MEXICO_CITY_TZ);
    const hUtc = hourIn('UTC');
    cachedIntlMexicoCityWorks = hMx != null && hUtc != null && hMx !== hUtc;
  } catch (_) {
    cachedIntlMexicoCityWorks = false;
  }
  return cachedIntlMexicoCityWorks;
}

function parseInstant(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const s = value.trim();
    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(\.\d{1,3})?$/.test(s) && !/[zZ]$/.test(s) && !/[+-]\d{2}:?\d{2}$/.test(s)) {
      const isoLike = s.includes('T') ? s : s.replace(' ', 'T');
      const d = new Date(`${isoLike}Z`);
      if (!Number.isNaN(d.getTime())) return d;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function mexicoCentralPartsFixedOffset(d) {
  const shifted = new Date(d.getTime() - 6 * 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return {
    day: pad(shifted.getUTCDate()),
    month: pad(shifted.getUTCMonth() + 1),
    year: String(shifted.getUTCFullYear()),
    hour: pad(shifted.getUTCHours()),
    minute: pad(shifted.getUTCMinutes()),
    second: pad(shifted.getUTCSeconds()),
  };
}

/**
 * Partes en tiempo del centro de México. Usa Intl solo si en este runtime
 * America/Mexico_City produce hora distinta de UTC (Hermes a veces no).
 */
function mexicoCentralParts(d, { seconds = false } = {}) {
  if (!intlMexicoCityTimezoneWorks()) {
    return mexicoCentralPartsFixedOffset(d);
  }
  const intlOpts = {
    timeZone: MEXICO_CITY_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(seconds ? { second: '2-digit' } : {}),
  };
  try {
    const parts = new Intl.DateTimeFormat('es-MX', intlOpts).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value ?? '';
    const day = get('day');
    const month = get('month');
    const year = get('year');
    const hour = get('hour');
    const minute = get('minute');
    const second = get('second');
    if (day && month && year && hour !== '' && minute !== '') {
      return {
        day,
        month,
        year,
        hour,
        minute,
        second: second || '00',
      };
    }
  } catch (_) {
    /* continuar */
  }
  return mexicoCentralPartsFixedOffset(d);
}

/** Fecha y hora instantánea del servidor (ISO) en horario de México: DD/MM/YYYY HH:mm */
export function formatFechaHoraMexicoCentral(value, options = {}) {
  const d = parseInstant(value);
  if (!d) return '';
  const p = mexicoCentralParts(d, options);
  return options.seconds
    ? `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}:${p.second}`
    : `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

/** Solo día/mes/año en México para un instante UTC (p. ej. created_at). */
export function formatFechaSoloMexicoCentralFromInstant(value) {
  const d = parseInstant(value);
  if (!d) return '';
  const p = mexicoCentralParts(d);
  return `${p.day}/${p.month}/${p.year}`;
}

/** Solo hora (y opcionalmente segundos) en México para un instante UTC. */
export function formatHoraMexicoCentralFromInstant(value, options = {}) {
  const d = parseInstant(value);
  if (!d) return '';
  const p = mexicoCentralParts(d, options);
  return options.seconds ? `${p.hour}:${p.minute}:${p.second}` : `${p.hour}:${p.minute}`;
}

/**
 * Formatea un DATE de PostgreSQL o string YYYY-MM-DD para mostrar en México
 * sin el desfase de un día al usar `new Date('YYYY-MM-DD')` (medianoche UTC).
 */
export function formatFechaOnlyMexico(value) {
  if (value == null || value === '') return '';
  let y;
  let m;
  let d;
  if (typeof value === 'string') {
    const part = value.split('T')[0];
    const parts = part.split('-');
    if (parts.length !== 3) return '';
    y = parseInt(parts[0], 10);
    m = parseInt(parts[1], 10);
    d = parseInt(parts[2], 10);
  } else if (value instanceof Date) {
    y = value.getUTCFullYear();
    m = value.getUTCMonth() + 1;
    d = value.getUTCDate();
  } else {
    return '';
  }
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return '';
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return dt.toLocaleDateString('es-MX', {
    timeZone: MEXICO_CITY_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
